import React, { useMemo, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";

function InnerPaymentForm({ onSucceeded, onError, profile, disabled, onBeforeConfirm }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (disabled) return;
    if (!stripe || !elements) return;
    setSubmitting(true);
    try {
      // Allow host page to persist allocations or other cart updates before confirming payment
      if (typeof onBeforeConfirm === 'function') {
        try {
          const ok = await onBeforeConfirm();
          if (ok === false) {
            setSubmitting(false);
            return;
          }
        } catch (e) {
          onError && onError(e?.message || 'Unable to prepare checkout');
          setSubmitting(false);
          return;
        }
      }

      const fullName = `${profile?.firstName || ""} ${profile?.lastName || ""}`.trim();
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: fullName || undefined,
              email: profile?.email || undefined,
            },
          },
        },
        redirect: "if_required",
      });

      if (result.error) {
        onError && onError(result.error.message || "Payment failed");
      } else if (result.paymentIntent) {
        const pi = result.paymentIntent;
        if (pi.status === "succeeded" || pi.status === "processing" || pi.status === "requires_capture") {
          onSucceeded && onSucceeded(pi);
        } else if (pi.status === "requires_action") {
          // 3DS or additional auth will trigger a redirect if needed when redirect: 'if_required'
          // If we land here without error, let the host page poll status or handle accordingly.
          onSucceeded && onSucceeded(pi);
        } else {
          onError && onError(`Unexpected status: ${pi.status}`);
        }
      }
    } catch (err) {
      onError && onError(err.message || "Payment failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2}>
        <PaymentElement options={{ layout: "tabs" }} />
        <Button
          type="submit"
          variant="contained"
          disabled={disabled || submitting || !stripe || !elements}
          startIcon={submitting ? <CircularProgress size={18} /> : null}
        >
          {submitting ? "Processing..." : "Confirm & Pay"}
        </Button>
        {disabled && (
          <Typography variant="caption" color="text.secondary">
            Please agree to the Terms to continue.
          </Typography>
        )}
        <div style={{height: 100}} />
      </Stack>
    </Box>
  );
}

export default function PaymentForm({ stripePromise, clientSecret, profile, onSucceeded, onError, disabled, onBeforeConfirm }) {
  const options = useMemo(() => ({
    clientSecret,
    appearance: { theme: "stripe" },
  }), [clientSecret]);

  if (!stripePromise) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          Stripe not initialized (missing publishable key).
        </Typography>
      </Box>
    );
  }

  if (!clientSecret) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          Preparing payment options…
        </Typography>
      </Box>
    );
  }

  return (
    <Elements key={clientSecret} stripe={stripePromise} options={options}>
      <InnerPaymentForm onSucceeded={onSucceeded} onError={onError} profile={profile} disabled={disabled} onBeforeConfirm={onBeforeConfirm} />
    </Elements>
  );
}
